import { useQuery } from "@apollo/client";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Alert,
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Fragment, useState } from "react";
import { Title, useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { CrmCard } from "../components/crm/CrmCard";
import { CrmPageHeader } from "../components/crm/CrmPageHeader";
import { RequirePermission } from "../components/RequirePermission";
import { ResourceName } from "../constants/resources";
import { CrmObject, MarketingOverviewDocument, type MarketingOverviewQuery } from "../generated/graphql";
import { CRM_COLORS, CRM_OBJECT_COLORS, formatCrmCount, formatCrmDateTime } from "../lib/crm";
import { conversionsPath, formatShortMonth, newLeadsPath, openLeadsPath } from "../lib/marketing";
import { SALESFORCE_UNITY_PALETTE } from "../lib/salesforceDashboard";

type Overview = MarketingOverviewQuery["marketingOverview"];
type Series = { label?: string | null; total: number; months: number[] };

const CHART_HEIGHT = 280;
// The chart stacks the largest sources and sums the rest.
const CHART_SOURCES = 6;
const OTHERS_COLOR = "#c9c7c5";

const daysFormat = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });

const cellSx = { fontSize: "13px", py: "6px", px: "10px", color: CRM_COLORS.text, whiteSpace: "nowrap" } as const;
const headerSx = { ...cellSx, backgroundColor: CRM_COLORS.tableHeader, color: CRM_COLORS.textWeak, fontWeight: 700 };
const linkSx = { color: CRM_COLORS.link, textDecoration: "none", "&:hover": { textDecoration: "underline" } };

const Tile = ({ label, value, to }: { label: string; value: string; to: string | null }) => {
  const navigate = useNavigate();
  const content = (
    <>
      <Typography sx={{ fontSize: "12px", color: CRM_COLORS.textWeak }}>{label}</Typography>
      <Typography sx={{ fontSize: "28px", fontWeight: 300, lineHeight: 1.1, color: CRM_COLORS.text }}>
        {value}
      </Typography>
    </>
  );
  const sx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
    p: "16px",
    backgroundColor: CRM_COLORS.cardBackground,
    border: `1px solid ${CRM_COLORS.cardBorder}`,
    borderTop: `3px solid ${CRM_OBJECT_COLORS[CrmObject.Lead]}`,
    borderRadius: "4px",
    boxShadow: CRM_COLORS.cardShadow,
    textAlign: "left",
  } as const;
  return to ? (
    <ButtonBase onClick={() => navigate(to)} sx={{ ...sx, "&:hover": { backgroundColor: CRM_COLORS.rowHover } }}>
      {content}
    </ButtonBase>
  ) : (
    <Box sx={sx}>{content}</Box>
  );
};

// A count that opens its leads for staff who may read the CRM; zero opens nothing.
const Count = ({ value, to, strong = false }: { value: number; to: string | null; strong?: boolean }) => {
  if (value === 0) return <Box sx={{ color: CRM_COLORS.label }}>–</Box>;
  const text = formatCrmCount(value);
  return to ? (
    <Link component={RouterLink} to={to} sx={{ ...linkSx, fontWeight: strong ? 700 : undefined }}>
      {text}
    </Link>
  ) : (
    <Box component="span" sx={{ fontWeight: strong ? 700 : undefined }}>
      {text}
    </Box>
  );
};

type MonthTableProps = {
  months: string[];
  labelHeader: string;
  rows: (Series & { details?: Series[] })[];
  none: string;
  // The lead list of a row, or of one of its details, in a month or all months.
  pathFor: ((row: Series, month: number | null, detail?: Series) => string) | null;
  footer?: { label: string; values: (string | null)[]; total: string | null };
};

const MonthTable = ({ months, labelHeader, rows, none, pathFor, footer }: MonthTableProps) => {
  const translate = useTranslate();
  const [expanded, setExpanded] = useState<Set<string | null | undefined>>(new Set());
  const toggle = (label: string | null | undefined) =>
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  const cells = (row: Series, detail?: Series) => {
    const series = detail ?? row;
    return (
      <>
        {series.months.map((count, index) => (
          <TableCell key={months[index]} align="right" sx={cellSx}>
            <Count value={count} to={pathFor && pathFor(row, index, detail)} />
          </TableCell>
        ))}
        <TableCell align="right" sx={{ ...cellSx, backgroundColor: CRM_COLORS.pageBackground }}>
          <Count value={series.total} to={pathFor && pathFor(row, null, detail)} strong />
        </TableCell>
      </>
    );
  };

  return (
    <TableContainer>
      <Table size="small" aria-label={labelHeader}>
        <TableHead>
          <TableRow>
            <TableCell sx={headerSx}>{labelHeader}</TableCell>
            {months.map(month => (
              <TableCell key={month} align="right" sx={headerSx}>
                {formatShortMonth(month)}
              </TableCell>
            ))}
            <TableCell align="right" sx={headerSx}>
              {translate("marketing.overview.total")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const label = row.label ?? none;
            const open = expanded.has(row.label);
            const hasDetails = (row.details?.length ?? 0) > 0;
            return (
              <Fragment key={label}>
                <TableRow hover>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                      {hasDetails && (
                        <IconButton
                          size="small"
                          onClick={() => toggle(row.label)}
                          aria-expanded={open}
                          aria-label={translate(
                            open ? "marketing.overview.hideDetails" : "marketing.overview.showDetails",
                            { source: label }
                          )}
                          sx={{ p: "2px", ml: "-6px" }}
                        >
                          <ChevronRightIcon
                            fontSize="small"
                            sx={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 120ms" }}
                          />
                        </IconButton>
                      )}
                      {label}
                    </Box>
                  </TableCell>
                  {cells(row)}
                </TableRow>
                {open &&
                  row.details?.map(detail => (
                    <TableRow key={`${label}-${detail.label ?? ""}`} sx={{ backgroundColor: "#fafaf9" }}>
                      <TableCell sx={{ ...cellSx, pl: "36px", color: CRM_COLORS.textWeak }}>
                        {detail.label ?? none}
                      </TableCell>
                      {cells(row, detail)}
                    </TableRow>
                  ))}
              </Fragment>
            );
          })}
          {footer && (
            <TableRow>
              <TableCell
                sx={{ ...cellSx, color: CRM_COLORS.textWeak, borderTop: `2px solid ${CRM_COLORS.cardBorder}` }}
              >
                {footer.label}
              </TableCell>
              {footer.values.map((value, index) => (
                <TableCell
                  key={months[index]}
                  align="right"
                  sx={{ ...cellSx, color: CRM_COLORS.textWeak, borderTop: `2px solid ${CRM_COLORS.cardBorder}` }}
                >
                  {value ?? "–"}
                </TableCell>
              ))}
              <TableCell
                align="right"
                sx={{
                  ...cellSx,
                  fontWeight: 700,
                  backgroundColor: CRM_COLORS.pageBackground,
                  borderTop: `2px solid ${CRM_COLORS.cardBorder}`,
                }}
              >
                {footer.total ?? "–"}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const NewLeadsChart = ({ overview, none, others }: { overview: Overview; none: string; others: string }) => {
  const shown = overview.newLeads.slice(0, CHART_SOURCES);
  const rest = overview.newLeads.slice(CHART_SOURCES);
  const keys = [...shown.map(source => source.label ?? none), ...(rest.length > 0 ? [others] : [])];
  const data = overview.months.map((month, index) => ({
    month: formatShortMonth(month),
    ...Object.fromEntries(shown.map(source => [source.label ?? none, source.months[index]])),
    ...(rest.length > 0 ? { [others]: rest.reduce((sum, source) => sum + source.months[index], 0) } : {}),
  }));
  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart data={data} margin={{ left: -16, right: 8 }}>
        <CartesianGrid vertical={false} stroke="rgba(197,211,223,0.5)" />
        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {keys.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            stackId="leads"
            fill={
              key === others ? OTHERS_COLOR : SALESFORCE_UNITY_PALETTE[(index * 2) % SALESFORCE_UNITY_PALETTE.length]
            }
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

const OpenByStatus = ({ overview, none, canOpen }: { overview: Overview; none: string; canOpen: boolean }) => {
  const translate = useTranslate();
  const largest = Math.max(1, ...overview.openByStatus.map(entry => entry.count));
  if (overview.openByStatus.length === 0) {
    return (
      <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>
        {translate("marketing.overview.noOpenLeads")}
      </Typography>
    );
  }
  return (
    <Box
      component="ul"
      aria-label={translate("marketing.overview.openByStatus")}
      sx={{ m: 0, p: 0, listStyle: "none" }}
    >
      {overview.openByStatus.map((entry, index) => (
        <Box
          component="li"
          key={entry.label ?? none}
          sx={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr) 48px", gap: "8px", py: "5px" }}
        >
          <Typography sx={{ fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {entry.label ?? none}
          </Typography>
          <Box
            sx={{ alignSelf: "center", height: 10, borderRadius: "2px", backgroundColor: CRM_COLORS.pathIncomplete }}
          >
            <Box
              sx={{
                width: `${(entry.count / largest) * 100}%`,
                height: "100%",
                borderRadius: "2px",
                backgroundColor: SALESFORCE_UNITY_PALETTE[index % SALESFORCE_UNITY_PALETTE.length],
              }}
            />
          </Box>
          <Box sx={{ fontSize: "13px", textAlign: "right" }}>
            <Count value={entry.count} to={canOpen ? openLeadsPath(entry.label) : null} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const OverviewContent = ({ overview }: { overview: Overview }) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>) => translate(`marketing.overview.${key}`, options);
  // The numbers open CRM lead lists only for staff who may read the CRM.
  const { canAccess: canReadCrm } = useCanAccess({ action: "list", resource: ResourceName.Crm });
  const canOpen = canReadCrm === true;
  const { months } = overview;
  const last = months.length - 1;
  const sum = (series: Series[], index: number | null) =>
    series.reduce((total, entry) => total + (index === null ? entry.total : entry.months[index]), 0);
  const days = (value: number | null | undefined) =>
    value === null || value === undefined ? null : t("days", { days: daysFormat.format(value) });
  const none = t("none");

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(auto-fit, minmax(170px, 1fr))" },
          gap: "12px",
          mb: "12px",
        }}
      >
        <Tile
          label={t("newLeadsThisMonth")}
          value={formatCrmCount(sum(overview.newLeads, last))}
          to={canOpen ? newLeadsPath(months, last) : null}
        />
        <Tile
          label={t("newLeads")}
          value={formatCrmCount(sum(overview.newLeads, null))}
          to={canOpen ? newLeadsPath(months, null) : null}
        />
        <Tile
          label={t("conversions")}
          value={formatCrmCount(sum(overview.conversions, null))}
          to={canOpen ? conversionsPath(months, null) : null}
        />
        <Tile label={t("medianDays")} value={days(overview.medianDaysToConvert) ?? "–"} to={null} />
        <Tile
          label={t("openLeads")}
          value={formatCrmCount(overview.openByStatus.reduce((total, entry) => total + entry.count, 0))}
          to={canOpen ? openLeadsPath() : null}
        />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(0, 1fr)" },
          gap: "12px",
          mb: "12px",
        }}
      >
        <CrmCard title={t("newLeadsChart")}>
          <NewLeadsChart overview={overview} none={none} others={t("others")} />
        </CrmCard>
        <CrmCard title={t("openByStatus")}>
          <OpenByStatus overview={overview} none={none} canOpen={canOpen} />
        </CrmCard>
      </Box>
      <Box sx={{ display: "grid", gap: "12px" }}>
        <CrmCard flush title={t("bySource")} testId="marketing-new-leads">
          {overview.newLeads.length === 0 ? (
            <Typography sx={{ px: "16px", pb: "12px", fontSize: "13px" }}>{t("noLeads")}</Typography>
          ) : (
            <MonthTable
              months={months}
              labelHeader={t("source")}
              rows={overview.newLeads}
              none={none}
              pathFor={
                canOpen
                  ? (row, month, detail) =>
                      newLeadsPath(months, month, row.label ?? null, detail ? (detail.label ?? null) : undefined)
                  : null
              }
            />
          )}
        </CrmCard>
        <CrmCard flush title={t("byTarget")} testId="marketing-conversions">
          {overview.conversions.length === 0 ? (
            <Typography sx={{ px: "16px", pb: "12px", fontSize: "13px" }}>{t("noConversions")}</Typography>
          ) : (
            <MonthTable
              months={months}
              labelHeader={t("target")}
              rows={overview.conversions}
              none={none}
              pathFor={canOpen ? (row, month) => conversionsPath(months, month, row.label ?? null) : null}
              footer={{
                label: t("medianRow"),
                values: overview.medianDaysToConvertByMonth.map(value =>
                  value === null || value === undefined ? null : daysFormat.format(value)
                ),
                total:
                  overview.medianDaysToConvert === null || overview.medianDaysToConvert === undefined
                    ? null
                    : daysFormat.format(overview.medianDaysToConvert),
              }}
            />
          )}
        </CrmCard>
      </Box>
    </>
  );
};

const MarketingOverviewContent = () => {
  const translate = useTranslate();
  const { data, loading, error } = useQuery<MarketingOverviewQuery>(MarketingOverviewDocument, {
    fetchPolicy: "cache-and-network",
  });
  const overview = data?.marketingOverview;
  return (
    <Box data-testid="marketing-overview">
      <Title title={translate("marketing.overview.title")} />
      <CrmPageHeader
        icon={
          <Box
            aria-hidden
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "4px",
              backgroundColor: CRM_OBJECT_COLORS[CrmObject.Lead],
              color: CRM_COLORS.iconForeground,
            }}
          >
            <CampaignOutlinedIcon />
          </Box>
        }
        overline={translate("marketing.title")}
        title={translate("marketing.overview.title")}
        meta={overview?.syncedAt && translate("crm.syncedAt", { time: formatCrmDateTime(overview.syncedAt) })}
      />
      {error && <Alert severity="error">{translate("marketing.loadError")}</Alert>}
      {loading && !overview && <CircularProgress aria-label={translate("crm.loading")} />}
      {overview && <OverviewContent overview={overview} />}
    </Box>
  );
};

export const MarketingOverviewPage = () => (
  <RequirePermission permission={ResourceName.Marketing}>
    <MarketingOverviewContent />
  </RequirePermission>
);
